import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Alert,
  Snackbar,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Tabs,
  Tab,
  Divider,
  Tooltip,
  Badge
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Security as SecurityIcon,
  Group as GroupIcon,
  Visibility as VisibilityIcon,
  Settings as SettingsIcon,
  Discount as DiscountIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon
} from '@mui/icons-material';

// Tab Panel Component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const UserRoles = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [roles, setRoles] = useState([]);
  const [pagePermissions, setPagePermissions] = useState([]);
  const [operationPermissions, setOperationPermissions] = useState([]);
  const [discountPermissions, setDiscountPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDiscountDialog, setOpenDiscountDialog] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [permissions, setPermissions] = useState([]);
  const [selectedPermissions, setSelectedPermissions] = useState([]);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'custom',
    isActive: true,
    pagePermissions: [],
    operationPermissions: []
  });

  const [discountFormData, setDiscountFormData] = useState({
    userId: '',
    maxDiscountRate: 0,
    maxDiscountAmount: 0,
    categoryRestrictions: [],
    brandRestrictions: [],
    requiresApproval: false,
    approvers: []
  });

  // Predefined page permissions for system management
  const predefinedPages = [
    { id: 1, name: 'Dashboard', path: '/dashboard', module: 'Ana Sayfa', description: 'Ana sayfa görüntüleme' },
    { id: 2, name: 'Kullanıcı Yönetimi', path: '/users', module: 'Sistem', description: 'Kullanıcı ekleme/düzenleme' },
    { id: 3, name: 'Rol Yönetimi', path: '/roles', module: 'Sistem', description: 'Rol yönetimi' },
    { id: 4, name: 'Firma Yönetimi', path: '/companies', module: 'Sistem', description: 'Firma yönetimi' },
    { id: 5, name: 'Tenant Yönetimi', path: '/tenants', module: 'Sistem', description: 'Tenant yönetimi' },
    { id: 6, name: 'Sistem Ayarları', path: '/settings', module: 'Sistem', description: 'Sistem ayarları' },
    { id: 7, name: 'Monitoring', path: '/monitoring', module: 'Sistem', description: 'Sistem izleme' },
    { id: 8, name: 'Raporlar', path: '/reports', module: 'Raporlar', description: 'Sistem raporları' },
    { id: 9, name: 'Entegrasyonlar', path: '/integrations', module: 'Entegrasyon', description: 'Entegrasyon yönetimi' },
    { id: 10, name: 'Backup Yönetimi', path: '/backups', module: 'Sistem', description: 'Yedekleme yönetimi' }
  ];

  // Predefined operation permissions for system management
  const predefinedOperations = [
    { id: 1, code: 'SYSTEM_USERS_CREATE', name: 'Sistem Kullanıcısı Oluştur', module: 'Sistem', description: 'Yeni sistem kullanıcısı oluşturma' },
    { id: 2, code: 'SYSTEM_USERS_EDIT', name: 'Sistem Kullanıcısı Düzenle', module: 'Sistem', description: 'Sistem kullanıcısı düzenleme' },
    { id: 3, code: 'SYSTEM_USERS_DELETE', name: 'Sistem Kullanıcısı Sil', module: 'Sistem', description: 'Sistem kullanıcısı silme' },
    { id: 4, code: 'TENANT_CREATE', name: 'Tenant Oluştur', module: 'Sistem', description: 'Yeni tenant oluşturma' },
    { id: 5, code: 'TENANT_DELETE', name: 'Tenant Sil', module: 'Sistem', description: 'Tenant silme' },
    { id: 6, code: 'SYSTEM_BACKUP', name: 'Sistem Yedekleme', module: 'Sistem', description: 'Sistem yedekleme işlemi' },
    { id: 7, code: 'SYSTEM_RESTORE', name: 'Sistem Geri Yükleme', module: 'Sistem', description: 'Sistem geri yükleme işlemi' },
    { id: 8, code: 'INTEGRATION_MANAGE', name: 'Entegrasyon Yönetimi', module: 'Entegrasyon', description: 'Entegrasyon yönetimi' }
  ];

  const permissionCategories = {
    'Sistem Yönetimi': [
      'system.users.view',
      'system.users.create',
      'system.users.edit',
      'system.users.delete',
      'system.roles.view',
      'system.roles.create',
      'system.roles.edit',
      'system.roles.delete',
      'system.companies.view',
      'system.companies.create',
      'system.companies.edit',
      'system.companies.delete'
    ],
    'Şirket Yönetimi': [
      'company.dashboard.view',
      'company.settings.view',
      'company.settings.edit',
      'company.users.view',
      'company.users.create',
      'company.users.edit',
      'company.users.delete'
    ],
    'Stok Yönetimi': [
      'stock.view',
      'stock.create',
      'stock.edit',
      'stock.delete',
      'stock.movements.view',
      'stock.movements.create',
      'warehouse.view',
      'warehouse.create',
      'warehouse.edit',
      'warehouse.delete'
    ],
    'Sipariş Yönetimi': [
      'orders.view',
      'orders.create',
      'orders.edit',
      'orders.delete',
      'orders.approve',
      'orders.reject',
      'invoices.view',
      'invoices.create',
      'invoices.edit',
      'invoices.delete',
      'invoices.send'
    ],
    'Üretim Yönetimi': [
      'production.view',
      'production.create',
      'production.edit',
      'production.delete',
      'production.recipes.view',
      'production.recipes.create',
      'production.recipes.edit',
      'production.recipes.delete',
      'workorders.view',
      'workorders.create',
      'workorders.edit',
      'workorders.delete'
    ],
    'Raporlar': [
      'reports.sales.view',
      'reports.inventory.view',
      'reports.financial.view',
      'reports.customer.view',
      'reports.production.view'
    ],
    'Entegrasyonlar': [
      'integration.pos.view',
      'integration.pos.edit',
      'integration.tecdoc.view',
      'integration.tecdoc.edit',
      'integration.einvoice.view',
      'integration.einvoice.edit',
      'integration.accounting.view',
      'integration.accounting.edit'
    ]
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    await Promise.all([
      fetchRoles(),
      fetchPagePermissions(),
      fetchOperationPermissions(),
      fetchDiscountPermissions()
    ]);
  };

  const fetchRoles = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/system/roles');
      if (response.ok) {
        const data = await response.json();
        setRoles(data);
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
      showSnackbar('Roller yüklenirken hata oluştu', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchPagePermissions = async () => {
    try {
      const response = await fetch('/api/system/page-permissions');
      if (response.ok) {
        const data = await response.json();
        setPagePermissions(data);
      }
    } catch (error) {
      console.error('Error fetching page permissions:', error);
    }
  };

  const fetchOperationPermissions = async () => {
    try {
      const response = await fetch('/api/system/operation-permissions');
      if (response.ok) {
        const data = await response.json();
        setOperationPermissions(data);
      }
    } catch (error) {
      console.error('Error fetching operation permissions:', error);
    }
  };

  const fetchDiscountPermissions = async () => {
    try {
      const response = await fetch('/api/system/discount-permissions');
      if (response.ok) {
        const data = await response.json();
        setDiscountPermissions(data);
      }
    } catch (error) {
      console.error('Error fetching discount permissions:', error);
    }
  };

  const fetchPermissions = async () => {
    try {
      const response = await fetch('/api/system/permissions');
      if (response.ok) {
        const data = await response.json();
        setPermissions(data);
      }
    } catch (error) {
      console.error('Error fetching permissions:', error);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleOpenDialog = (role = null) => {
    if (role) {
      setEditingRole(role);
      setFormData({
        name: role.name,
        description: role.description,
        type: role.type || 'custom',
        isActive: role.isActive,
        pagePermissions: role.pagePermissions || [],
        operationPermissions: role.operationPermissions || []
      });
      setSelectedPermissions(role.permissions || []);
    } else {
      setEditingRole(null);
      setFormData({
        name: '',
        description: '',
        type: 'custom',
        isActive: true,
        pagePermissions: [],
        operationPermissions: []
      });
      setSelectedPermissions([]);
    }
    setOpenDialog(true);
  };

  const handleAddDiscountPermission = () => {
    setSelectedUser(null);
    setDiscountFormData({
      userId: '',
      maxDiscountRate: 0,
      maxDiscountAmount: 0,
      categoryRestrictions: [],
      brandRestrictions: [],
      requiresApproval: false,
      approvers: []
    });
    setOpenDiscountDialog(true);
  };

  const handleSaveRole = async () => {
    try {
      const url = editingRole 
        ? `/api/system/roles/${editingRole.id}`
        : '/api/system/roles';
      
      const method = editingRole ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        showSnackbar(editingRole ? 'Rol güncellendi' : 'Rol oluşturuldu');
        setOpenDialog(false);
        loadData();
      } else {
        throw new Error('İşlem başarısız');
      }
    } catch (error) {
      showSnackbar('Rol kaydedilirken hata oluştu', 'error');
    }
  };

  const handleSaveDiscountPermission = async () => {
    try {
      const url = selectedUser 
        ? `/api/system/discount-permissions/${selectedUser.id}`
        : '/api/system/discount-permissions';
      
      const method = selectedUser ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(discountFormData)
      });

      if (response.ok) {
        showSnackbar(selectedUser ? 'İskonto izni güncellendi' : 'İskonto izni oluşturuldu');
        setOpenDiscountDialog(false);
        loadData();
      } else {
        throw new Error('İşlem başarısız');
      }
    } catch (error) {
      showSnackbar('İskonto izni kaydedilirken hata oluştu', 'error');
    }
  };

  const handleDeleteRole = async (roleId) => {
    if (window.confirm('Bu rolü silmek istediğinizden emin misiniz?')) {
    try {
      const response = await fetch(`/api/system/roles/${roleId}`, {
          method: 'DELETE'
      });

      if (response.ok) {
          showSnackbar('Rol silindi');
          loadData();
      } else {
          throw new Error('Silme işlemi başarısız');
      }
    } catch (error) {
      showSnackbar('Rol silinirken hata oluştu', 'error');
      }
    }
  };

  const handlePagePermissionChange = (pageId, action, checked) => {
    const newPagePermissions = [...(formData.pagePermissions || [])];
    const existingIndex = newPagePermissions.findIndex(p => p.pagePermissionId === pageId);
    
    if (existingIndex >= 0) {
      newPagePermissions[existingIndex] = {
        ...newPagePermissions[existingIndex],
        [action]: checked
      };
    } else {
      newPagePermissions.push({
        pagePermissionId: pageId,
        [action]: checked
      });
    }
    
    setFormData({ ...formData, pagePermissions: newPagePermissions });
  };

  const handleOperationPermissionChange = (operationId, checked, maxAmount = 0, minAmount = 0) => {
    const newOperationPermissions = [...(formData.operationPermissions || [])];
    const existingIndex = newOperationPermissions.findIndex(p => p.operationPermissionId === operationId);
    
    if (existingIndex >= 0) {
      if (checked) {
        newOperationPermissions[existingIndex] = {
          ...newOperationPermissions[existingIndex],
          canExecute: checked,
          maxAmount,
          minAmount
        };
      } else {
        newOperationPermissions.splice(existingIndex, 1);
      }
    } else if (checked) {
      newOperationPermissions.push({
        operationPermissionId: operationId,
        canExecute: checked,
        maxAmount,
        minAmount
      });
    }
    
    setFormData({ ...formData, operationPermissions: newOperationPermissions });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          <SecurityIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Sistem Kullanıcı Rolleri
        </Typography>
        <Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
            sx={{ mr: 1 }}
        >
          Yeni Rol
        </Button>
          <Button
            variant="outlined"
            startIcon={<DiscountIcon />}
            onClick={handleAddDiscountPermission}
          >
            İskonto İzni
          </Button>
        </Box>
      </Box>

      <Paper sx={{ width: '100%' }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="system user management tabs">
          <Tab label="Roller" icon={<GroupIcon />} />
          <Tab label="Sayfa İzinleri" icon={<VisibilityIcon />} />
          <Tab label="İşlem İzinleri" icon={<SettingsIcon />} />
          <Tab label="İskonto İzinleri" icon={<DiscountIcon />} />
          <Tab label="Sistem Özeti" icon={<PersonIcon />} />
        </Tabs>

        <TabPanel value={activeTab} index={0}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Rol Adı</TableCell>
                  <TableCell>Açıklama</TableCell>
                  <TableCell>Tip</TableCell>
                  <TableCell>Durum</TableCell>
                  <TableCell>Kullanıcı Sayısı</TableCell>
                  <TableCell>Sayfa İzinleri</TableCell>
                  <TableCell>İşlem İzinleri</TableCell>
                  <TableCell>İşlemler</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {roles.map((role) => (
                  <TableRow key={role.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <GroupIcon />
                        {role.name}
                      </Box>
                    </TableCell>
                    <TableCell>{role.description}</TableCell>
                    <TableCell>
                      <Chip 
                        label={role.type || 'custom'} 
                        color={role.type === 'predefined' ? 'primary' : 'default'}
                        size="small" 
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={role.isActive ? 'Aktif' : 'Pasif'}
                        color={role.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip label={role.userCount || 0} size="small" />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={`${role.pagePermissions?.length || 0} sayfa`} 
                        size="small" 
                        color="info"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={`${role.operationPermissions?.length || 0} işlem`} 
                        size="small"
                        color="secondary"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton onClick={() => handleOpenDialog(role)}>
                        <EditIcon />
                      </IconButton>
                      {role.type !== 'predefined' && (
                      <IconButton
                          color="error" 
                        onClick={() => handleDeleteRole(role.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <Typography variant="h6" gutterBottom>
            Sayfa Bazlı İzinler
          </Typography>
          <Grid container spacing={2}>
            {predefinedPages.map((page) => (
              <Grid item xs={12} md={6} key={page.id}>
                <Card>
                  <CardContent>
                    <Typography variant="h6">{page.name}</Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {page.description}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Modül: {page.module} | Yol: {page.path}
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <FormControlLabel
                            control={<Checkbox size="small" />}
                            label="Görüntüle"
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <FormControlLabel
                            control={<Checkbox size="small" />}
                            label="Oluştur"
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <FormControlLabel
                            control={<Checkbox size="small" />}
                            label="Düzenle"
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <FormControlLabel
                            control={<Checkbox size="small" />}
                            label="Sil"
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <FormControlLabel
                            control={<Checkbox size="small" />}
                            label="Dışa Aktar"
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <FormControlLabel
                            control={<Checkbox size="small" />}
                            label="İçe Aktar"
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          <Typography variant="h6" gutterBottom>
            İşlem Bazlı İzinler
          </Typography>
          <Grid container spacing={2}>
            {predefinedOperations.map((operation) => (
              <Grid item xs={12} md={6} key={operation.id}>
                <Card>
                  <CardContent>
                    <Typography variant="h6">{operation.name}</Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {operation.description}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Kod: {operation.code} | Modül: {operation.module}
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                      <FormControlLabel
                        control={<Checkbox size="small" />}
                        label="İşlemi Yapabilir"
                      />
                      <Grid container spacing={1} sx={{ mt: 1 }}>
                        <Grid item xs={6}>
                          <TextField
                            size="small"
                            label="Maks. Tutar"
                            type="number"
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <TextField
                            size="small"
                            label="Min. Tutar"
                            type="number"
                            fullWidth
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </TabPanel>

        <TabPanel value={activeTab} index={3}>
          <Typography variant="h6" gutterBottom>
            İskonto İzinleri
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Kullanıcı</TableCell>
                  <TableCell>Maks. Oran (%)</TableCell>
                  <TableCell>Maks. Tutar</TableCell>
                  <TableCell>Kategori Kısıtlaması</TableCell>
                  <TableCell>Marka Kısıtlaması</TableCell>
                  <TableCell>Onay Gerekli</TableCell>
                  <TableCell>Durum</TableCell>
                  <TableCell>İşlemler</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {discountPermissions.map((permission) => (
                  <TableRow key={permission.userId}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon />
                        {permission.userName}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip label={`%${permission.maxDiscountRate}`} color="primary" />
                    </TableCell>
                    <TableCell>
                      <Chip label={`₺${permission.maxDiscountAmount}`} color="secondary" />
                    </TableCell>
                    <TableCell>
                      {permission.categoryRestrictions?.length > 0 ? (
                        <Chip label={`${permission.categoryRestrictions.length} kategori`} size="small" />
                      ) : (
                        <Chip label="Yok" size="small" color="default" />
                      )}
                    </TableCell>
                    <TableCell>
                      {permission.brandRestrictions?.length > 0 ? (
                        <Chip label={`${permission.brandRestrictions.length} marka`} size="small" />
                      ) : (
                        <Chip label="Yok" size="small" color="default" />
                      )}
                    </TableCell>
                    <TableCell>
                      {permission.requiresApproval ? (
                        <CheckCircleIcon color="warning" />
                      ) : (
                        <CheckCircleIcon color="success" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip label="Aktif" color="success" size="small" />
                    </TableCell>
                    <TableCell>
                      <IconButton>
                        <EditIcon />
                      </IconButton>
                      <IconButton color="error">
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        <TabPanel value={activeTab} index={4}>
          <Typography variant="h6" gutterBottom>
            Sistem Kullanıcı Özeti
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Sistem İstatistikleri
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Box textAlign="center">
                        <Typography variant="h4" color="primary">
                          {roles.length}
                        </Typography>
                        <Typography variant="body2">Toplam Rol</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box textAlign="center">
                        <Typography variant="h4" color="secondary">
                          {pagePermissions.length}
                        </Typography>
                        <Typography variant="body2">Sayfa İzni</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box textAlign="center">
                        <Typography variant="h4" color="info">
                          {operationPermissions.length}
                        </Typography>
                        <Typography variant="body2">İşlem İzni</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box textAlign="center">
                        <Typography variant="h4" color="success">
                          {discountPermissions.length}
                        </Typography>
                        <Typography variant="body2">İskonto İzni</Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Son Aktiviteler
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleIcon color="success" />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Yeni rol oluşturuldu" 
                        secondary="Admin rolü 2 saat önce oluşturuldu" 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleIcon color="success" />
                      </ListItemIcon>
                      <ListItemText 
                        primary="İskonto izni güncellendi" 
                        secondary="Kullanıcı A için iskonto izni güncellendi" 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <WarningIcon color="warning" />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Rol silindi" 
                        secondary="Test rolü 1 gün önce silindi" 
                      />
                    </ListItem>
                  </List>
        </CardContent>
      </Card>
            </Grid>
          </Grid>
        </TabPanel>
      </Paper>

      {/* Add/Edit Role Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          {editingRole ? 'Rol Düzenle' : 'Yeni Rol Ekle'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Rol Adı"
                value={formData.name || ''}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Rol Tipi</InputLabel>
                <Select
                  value={formData.type || 'custom'}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                >
                  <MenuItem value="predefined">Önceden Tanımlı</MenuItem>
                  <MenuItem value="custom">Özel</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Açıklama"
                multiline
                rows={2}
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive || false}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                }
                label="Aktif"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Sayfa İzinleri
              </Typography>
              <Grid container spacing={2}>
                {predefinedPages.map((page) => (
                  <Grid item xs={12} md={6} key={page.id}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle1">{page.name}</Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {page.description}
              </Typography>
                        <Grid container spacing={1}>
                          <Grid item xs={6}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  size="small"
                                  checked={formData.pagePermissions?.find(p => p.pagePermissionId === page.id)?.canView || false}
                                  onChange={(e) => handlePagePermissionChange(page.id, 'canView', e.target.checked)}
                                />
                              }
                              label="Görüntüle"
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  size="small"
                                  checked={formData.pagePermissions?.find(p => p.pagePermissionId === page.id)?.canCreate || false}
                                  onChange={(e) => handlePagePermissionChange(page.id, 'canCreate', e.target.checked)}
                                />
                              }
                              label="Oluştur"
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  size="small"
                                  checked={formData.pagePermissions?.find(p => p.pagePermissionId === page.id)?.canEdit || false}
                                  onChange={(e) => handlePagePermissionChange(page.id, 'canEdit', e.target.checked)}
                                />
                              }
                              label="Düzenle"
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <FormControlLabel
                              control={
                            <Checkbox
                                  size="small"
                                  checked={formData.pagePermissions?.find(p => p.pagePermissionId === page.id)?.canDelete || false}
                                  onChange={(e) => handlePagePermissionChange(page.id, 'canDelete', e.target.checked)}
                                />
                              }
                              label="Sil"
                            />
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>İptal</Button>
          <Button onClick={handleSaveRole} variant="contained">Kaydet</Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Discount Permission Dialog */}
      <Dialog open={openDiscountDialog} onClose={() => setOpenDiscountDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedUser ? 'İskonto İzni Düzenle' : 'Yeni İskonto İzni'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Kullanıcı</InputLabel>
                <Select
                  value={discountFormData.userId || ''}
                  onChange={(e) => setDiscountFormData({ ...discountFormData, userId: e.target.value })}
                >
                  <MenuItem value="user1">Sistem Kullanıcısı 1</MenuItem>
                  <MenuItem value="user2">Sistem Kullanıcısı 2</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Maksimum İskonto Oranı (%)"
                type="number"
                value={discountFormData.maxDiscountRate || ''}
                onChange={(e) => setDiscountFormData({ ...discountFormData, maxDiscountRate: parseFloat(e.target.value) })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Maksimum İskonto Tutarı (₺)"
                type="number"
                value={discountFormData.maxDiscountAmount || ''}
                onChange={(e) => setDiscountFormData({ ...discountFormData, maxDiscountAmount: parseFloat(e.target.value) })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={discountFormData.requiresApproval || false}
                    onChange={(e) => setDiscountFormData({ ...discountFormData, requiresApproval: e.target.checked })}
                  />
                }
                label="Onay Gerekli"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Kategori Kısıtlamaları (virgülle ayırın)"
                value={discountFormData.categoryRestrictions?.join(', ') || ''}
                onChange={(e) => setDiscountFormData({ 
                  ...discountFormData, 
                  categoryRestrictions: e.target.value.split(',').map(s => s.trim()).filter(s => s)
                })}
                helperText="Örnek: Motor Yağları, Fren Sistemleri"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Marka Kısıtlamaları (virgülle ayırın)"
                value={discountFormData.brandRestrictions?.join(', ') || ''}
                onChange={(e) => setDiscountFormData({ 
                  ...discountFormData, 
                  brandRestrictions: e.target.value.split(',').map(s => s.trim()).filter(s => s)
                })}
                helperText="Örnek: Castrol, Brembo"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDiscountDialog(false)}>İptal</Button>
          <Button onClick={handleSaveDiscountPermission} variant="contained">Kaydet</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserRoles;
